import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/WhyWeStarted.css';
import QuoteModal from './QuoteModal';

const WhyWeStarted = () => {
  const navigate = useNavigate();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const openQuoteModal = () => {
    setIsQuoteModalOpen(true);
  };

  const closeQuoteModal = () => {
    setIsQuoteModalOpen(false);
  };

  const handleTalkToFounder = () => {
    navigate('/contact');
  };

  const problems = [
    {
      title: 'Long Service Delays',
      description: 'Patients waited weeks or months for hearing aid repairs. Clinics had to send products back to manufacturers, causing frustration and lost trust.',
      icon: '⏰',
      color: '#e74c3c'
    },
    {
      title: 'High Costs',
      description: 'Quality hearing aids were unaffordable for most Indians. Imported products came with heavy markups and duties.',
      icon: '💰',
      color: '#e67e22'
    },
    {
      title: 'No Brand Identity',
      description: 'Distributors and clinics sold generic products. They had no way to build their own brand or stand out in the market.',
      icon: '🏷️',
      color: '#f39c12'
    },
    {
      title: 'Poor After-Sales Support',
      description: 'Manufacturers disappeared after selling products. No training, no marketing support, no partnership.',
      icon: '📞',
      color: '#e74c3c'
    }
  ];

  const solutions = [
    {
      title: 'Affordable Excellence',
      description: 'Premium quality hearing aids at prices that make sense for Indian customers and profitable for partners.',
      icon: '🎯',
      stat: '40-50% Lower Prices'
    },
    {
      title: 'Instant Service Revolution',
      description: 'Clinic becomes the service center. Patients get instant replacement. No waiting. No frustration.',
      icon: '⚡',
      stat: 'Same Day Resolution'
    },
    {
      title: 'BYOB - Build Your Own Brand',
      description: 'Clinics and distributors create their own branded hearing aids. Custom packaging. Your logo. Your identity.',
      icon: '🎨',
      stat: '100% Private Label'
    },
    {
      title: 'Complete Partner Support',
      description: 'Training, marketing materials, technical support, and dedicated relationship manager.',
      icon: '🤝',
      stat: '24/7 Support'
    }
  ];

  const originStory = {
    instantService: {
      title: 'The "Instant Service" Concept',
      story: 'We saw patients traveling 100+ km for repairs, waiting 45 days, then getting their hearing aids back still not working properly. We thought — why can\'t the clinic fix it instantly? Why can\'t we just replace it on the spot? That\'s when the concept was born.',
      impact: 'Now clinics can replace hearing aids instantly. Customers walk out happy. Trust is built.',
      icon: '💡'
    },
    byob: {
      title: 'The BYOB Inspiration',
      story: 'Distributors told us — "We sell your products, but customers don\'t remember us. They remember your brand." That hit hard. We realized partners need their own identity. So we created BYOB — Build Your Own Brand.',
      impact: 'Now partners build their own legacy. Their brand. Their customers. Their success.',
      icon: '🚀'
    }
  };

  return (
    <>
      <section id="why-we-started" className="why-we-started-section">
        <div className="why-we-started-container">
          
          {/* Section Header */}
          <div className="why-we-started-header">
            <span className="why-we-started-tagline">Our Origin Story</span>
            <h2 className="why-we-started-title">
              Why We <span className="why-we-started-highlight">Started</span>
            </h2>
            <div className="why-we-started-underline"></div>
            <p className="why-we-started-subtitle">
              Every revolution starts with a problem. Here's what we saw, and how we decided to fix it.
            </p>
          </div>

          {/* The Problem Section */}
          <div className="why-we-started-problem">
            <div className="why-we-started-problem-header">
              <div className="why-we-started-problem-icon">⚠️</div>
              <h3>The Problem We Saw</h3>
              <p>The hearing aid industry was broken. Here's what needed to change.</p>
            </div>
            <div className="why-we-started-problem-grid">
              {problems.map((problem, index) => (
                <div key={index} className="why-we-started-problem-card">
                  <div className="why-we-started-problem-icon-wrapper" style={{ background: `${problem.color}15` }}>
                    <span className="why-we-started-problem-card-icon">{problem.icon}</span>
                  </div>
                  <h4>{problem.title}</h4>
                  <p>{problem.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* The Solution Section */}
          <div className="why-we-started-solution">
            <div className="why-we-started-solution-header">
              <div className="why-we-started-solution-icon">💡</div>
              <h3>Our Solution</h3>
              <p>We didn't just complain. We built solutions.</p>
            </div>
            <div className="why-we-started-solution-grid">
              {solutions.map((solution, index) => (
                <div key={index} className="why-we-started-solution-card">
                  <div className="why-we-started-solution-icon-wrapper">
                    <span>{solution.icon}</span>
                  </div>
                  <div className="why-we-started-solution-content">
                    <h4>{solution.title}</h4>
                    <p>{solution.description}</p>
                    <div className="why-we-started-solution-stat">{solution.stat}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Origin Stories */}
          <div className="why-we-started-origin">
            <div className="why-we-started-origin-grid">
              {/* Instant Service Story */}
              <div className="why-we-started-origin-card instant">
                <div className="why-we-started-origin-icon">{originStory.instantService.icon}</div>
                <h3>{originStory.instantService.title}</h3>
                <div className="why-we-started-origin-story">
                  <p>{originStory.instantService.story}</p>
                </div>
                <div className="why-we-started-origin-impact">
                  <span className="why-we-started-origin-impact-icon">✓</span>
                  <p>{originStory.instantService.impact}</p>
                </div>
                <div className="why-we-started-origin-badge">Revolutionary Concept</div>
              </div>

              {/* BYOB Story */}
              <div className="why-we-started-origin-card byob">
                <div className="why-we-started-origin-icon">{originStory.byob.icon}</div>
                <h3>{originStory.byob.title}</h3>
                <div className="why-we-started-origin-story">
                  <p>{originStory.byob.story}</p>
                </div>
                <div className="why-we-started-origin-impact">
                  <span className="why-we-started-origin-impact-icon">✓</span>
                  <p>{originStory.byob.impact}</p>
                </div>
                <div className="why-we-started-origin-badge">Industry First</div>
              </div>
            </div>
          </div>

          {/* The Promise */}
          <div className="why-we-started-promise">
            <div className="why-we-started-promise-content">
              <div className="why-we-started-promise-icon">🎯</div>
              <h3>Our Promise to You</h3>
              <p>
                We will never compromise on quality. We will always put our partners first. 
                We will continue to innovate. And we will help you build a legacy, not just a business.
              </p>
              <div className="why-we-started-promise-features">
                <span>✓ Quality First</span>
                <span>✓ Partner Success</span>
                <span>✓ Continuous Innovation</span>
                <span>✓ Lifetime Support</span>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="why-we-started-cta">
            <div className="why-we-started-cta-content">
              <h3>Join the Revolution</h3>
              <p>Be part of India's most innovative hearing aid journey</p>
              <div className="why-we-started-cta-buttons">
                <button onClick={openQuoteModal} className="why-we-started-cta-primary">
                  Become a Partner
                  <svg className="why-we-started-cta-icon" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button onClick={handleTalkToFounder} className="why-we-started-cta-secondary">
                  Talk to Founder
                </button>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Quote Modal */}
      <QuoteModal isOpen={isQuoteModalOpen} onClose={closeQuoteModal} />
    </>
  );
};

export default WhyWeStarted;